import { Track } from './track.js';
import { TICK_RATE } from './constants.js';

/**
 * Per-ship race progression. Progress is tracked as a continuous monotonic distance
 * (meters since the start line); checkpoints and laps are thresholds along it. This
 * makes ordering, placement, wrong-way detection and validation all fall out of a
 * single number that the (authoritative) simulation owns.
 */
export class RaceProgress {
  /** Total corrected progress in meters since race start (can be negative on the grid). */
  total: number;
  /** Current lap, 1-based. */
  lap = 1;
  /** Next checkpoint index to cross (into track.checkpoints). */
  nextCheckpoint = 1;
  lapTimes: number[] = [];
  bestLapTicks = 0;
  lapStartTick = 0;
  finishTick = 0;
  finished = false;
  /** Split tick for each checkpoint of the current lap. */
  splits: number[] = [];
  wrongWayAmount = 0;
  wrongWay = false;

  private lastProgress: number;

  constructor(
    readonly track: Track,
    readonly totalLaps: number,
    initialProgress: number
  ) {
    this.total = initialProgress > track.length / 2 ? initialProgress - track.length : initialProgress;
    this.lastProgress = initialProgress;
  }

  /** Feed the ship's current raw track progress (meters, 0..track.length). */
  update(rawProgress: number, tick: number, respawning: boolean): { checkpoint?: number; lapDone?: boolean } {
    const out: { checkpoint?: number; lapDone?: boolean } = {};
    if (this.finished) {
      this.lastProgress = rawProgress;
      return out;
    }
    let delta = this.track.mainDelta(this.lastProgress, rawProgress);
    // teleport guard: a legit sim step never moves more than ~4m/tick at max speed.
    // Respawns are the only legitimate snap (backwards to a checkpoint).
    if (Math.abs(delta) > 12 && !respawning) delta = 0;
    this.lastProgress = rawProgress;
    this.total += delta;

    // wrong-way detection (persistent negative motion while racing)
    if (delta < -0.02 && !respawning) {
      this.wrongWayAmount = Math.min(60, this.wrongWayAmount - delta);
    } else if (delta > 0.05) {
      this.wrongWayAmount = Math.max(0, this.wrongWayAmount - delta * 2);
    }
    this.wrongWay = this.wrongWayAmount > 18;

    // checkpoint / lap thresholds
    const cps = this.track.checkpoints;
    const L = this.track.length;
    // open splines (sprints) clamp s at L-ε, so the finish line sits a hair early
    const finishMargin = this.track.main.closed ? 0 : 6;
    let guard = 0;
    while (guard++ < 8) {
      const lapBase = (this.lap - 1) * L;
      const isLapLine = this.nextCheckpoint === 0;
      const threshold = (isLapLine ? this.lap * L : lapBase + cps[this.nextCheckpoint]) - (isLapLine ? finishMargin : 0);
      if (this.total < threshold) break;
      if (isLapLine) {
        const lapTicks = tick - this.lapStartTick;
        this.lapTimes.push(lapTicks);
        if (this.bestLapTicks === 0 || lapTicks < this.bestLapTicks) this.bestLapTicks = lapTicks;
        this.lapStartTick = tick;
        this.lap++;
        this.splits = [];
        out.lapDone = true;
        if (this.lap > this.totalLaps) {
          this.finished = true;
          this.finishTick = tick;
          break;
        }
      } else {
        this.splits.push(tick);
        out.checkpoint = this.nextCheckpoint;
      }
      this.nextCheckpoint = (this.nextCheckpoint + 1) % cps.length;
    }
    return out;
  }

  /** Fraction through the current lap 0..1 (for HUD). */
  lapFraction(): number {
    const L = this.track.length;
    const inLap = this.total - (this.lap - 1) * L;
    return Math.max(0, Math.min(1, inLap / L));
  }
}

export function ticksToTimeString(ticks: number): string {
  if (ticks <= 0) return '--:--.---';
  const ms = Math.round((ticks / TICK_RATE) * 1000);
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const milli = ms % 1000;
  return `${m}:${String(s).padStart(2, '0')}.${String(milli).padStart(3, '0')}`;
}

export function secondsToTicks(sec: number): number {
  return Math.round(sec * TICK_RATE);
}

export interface StandingEntry {
  shipIndex: number;
  finished: boolean;
  eliminated: boolean;
  /** Sort key: finished ships by finish tick, others by progress. */
  finishTick: number;
  total: number;
}

/** Compute live placements. Returns ship indices in placement order. */
export function computeStandings(entries: StandingEntry[]): number[] {
  const sorted = [...entries].sort((a, b) => {
    if (a.eliminated !== b.eliminated) return a.eliminated ? 1 : -1;
    if (a.finished && b.finished) return a.finishTick - b.finishTick;
    if (a.finished !== b.finished) return a.finished ? -1 : 1;
    return b.total - a.total;
  });
  return sorted.map((e) => e.shipIndex);
}
